import { headers, logPrefix } from './constants';
import JSONResponseHandler from '../serviceHelpers/JSONResponseHandler';
import { APIErrorResponse, isAPIErrorResponse } from '../serviceHelpers/APIErrorResponse';
import Identity from './identity';
import { isUserSegment, BaseUserSegment } from './userSegments';

/**
 * Gets the user segments.
 *
 *
 * @returns
 * A __Promise__ with user segments info data. When successful, the user segments along with the timestamp of when they were fetched should be stored in browser storage.
 *
 * <details>
 * <summary>
 * On success (resolves)
 * </summary>
 *
 * ```json
 * {
 *     "id": "851082314894535",
 *     "segment": "high"
 * }
 * ```
 * </details>
 *
 * <details>
 * <summary>
 * On error (throws)
 * </summary>
 *
 * ```
 * See documentation for corresponding error codes:
 * https://redirector.arcpublishing.com/alc/docs/swagger/?url=./arc-products/arc-identity-v1.json#/Authentication/authToken
 *
 * ```
 * </details>
 *
 */

export default async function getUserSegments(): Promise<BaseUserSegment | APIErrorResponse> {
  const isLoggedIn = await Identity.isLoggedIn();
  if (!isLoggedIn) {
    throw new Error(`${logPrefix} Missing or invalid jwt`);
  }

  return fetch(`${Identity.apiOrigin}/identity/public/v2/identity/segmentation`, {
    method: 'GET',
    cache: 'no-cache',
    headers: {
      Authorization: `Bearer ${Identity.userIdentity.accessToken}`,
      ...headers
    }
  })
    .then(JSONResponseHandler)
    .then(json => {
      let userSegments = {
        updated: new Date(),
        uuid: Identity.userIdentity.uuid,
        segments: {}
      };
      if (isUserSegment(json)) {
        Identity.userSegments = {
          ...userSegments,
          segments: json
        };
        return json;
      } else {
        if (!isAPIErrorResponse(json)) {
          Identity.userSegments = userSegments;
          return {};
        } else {
          throw json;
        }
      }
    });
}
